import type { Principal } from '../../types/auth';
import type {
    BodyChunk,
    HeaderValue,
    HeaderValues,
    HttpCookie,
    HttpMethod,
    HttpStatusCode,
    MutableHttpHeaders,
    ReadonlyHeaderValues,
    ReadonlyHttpHeaders,
    ResponseCookie
} from '../../types/web/http'

import type {
    ServerHttpRequest,
    ServerHttpResponse,
    ServerWebExchange
} from '../../types/web/server';
import { AbstractHttpHeaders, AbstractHttpRequest, AbstractHttpResponse } from '../http/exchange.ts';
import { httpStatusCode } from '../http/status.ts';
import net from 'node:net';
import http from 'node:http';
import http2 from 'node:http2';

export class ExtendedHttpIncomingMessage extends http.IncomingMessage {
    // circular reference to the exchange
    exchange?: ServerWebExchange;

    upgradeHead?: Buffer<ArrayBufferLike>


    get urlBang(): string {
        return this.url!;
    }

    get socketEncrypted(): boolean {
        // see tls.TLSSocket#encrypted
        return this.socket['encrypted'] === true;
    }
}

export class ExtendedHttpServerResponse<Request extends ExtendedHttpIncomingMessage = ExtendedHttpIncomingMessage> extends http.ServerResponse<Request> {

    markHeadersSent(): void {
        this['_header'] = true; // prevent response from being sent
    }

    getRawHeaderNames(): string[] {
        return super['getRawHeaderNames']();
    }
}

abstract class AbstractServerHttpRequest extends AbstractHttpRequest<ReadonlyHttpHeaders> {
    abstract getNativeRequest<T>(): T;
}

export abstract class AbstractServerHttpResponse extends AbstractHttpResponse<MutableHttpHeaders> {
    #cookies: ResponseCookie[] = [];
    #statusCode?: HttpStatusCode;
    #state: 'new' | 'committing' | 'commit-action-failed' | 'committed' = 'new';
    #commitActions: (() => Promise<void>)[] = [];

    setStatusCode(statusCode?: HttpStatusCode): boolean {
        if (this.#state === 'committed') {
           return false;
        }
        else {
            this.#statusCode = statusCode;
            return true;
        }
    }

    setRawStatusCode(statusCode?: number): boolean {
        return this.setStatusCode(statusCode === undefined ? undefined : httpStatusCode(statusCode));
    };

    get statusCode(): HttpStatusCode | undefined {
        return this.#statusCode;
    }

    addCookie(cookie: ResponseCookie): this {
        if (this.#state === 'committed') {
            throw new Error(`Cannot add cookie ${JSON.stringify(cookie)} because HTTP response has already been committed`);
        }
        this.#cookies.push(cookie);
        return this;
    }

    abstract getNativeResponse<T>(): T;

    beforeCommit(action: () => Promise<void>): void {
        this.#commitActions.push(action);
    }

    get commited(): boolean {
        const state = this.#state;
        return state !== 'new' && state !== 'commit-action-failed';
    }

    async body(body: ReadableStream<BodyChunk> | Promise<BodyChunk | void> | BodyChunk): Promise<boolean> {
        if (body instanceof ReadableStream) {
            throw new Error('ReadableStream body not supported yet');
        }
        const buffer = await body;
        try {
            // touch buffers if needed

            return await this.doCommit(async () => {

                return await this.bodyInternal(Promise.resolve(buffer));

            }).catch(error => {
                // release buffers
                throw error;
            });
        }
        catch (error) {
            // clear content headers
            throw error;
        }
    }

    abstract bodyInternal(body: Promise<BodyChunk | void>): Promise<boolean>;

    async end(): Promise<boolean> {
        if (!this.commited) {
            return this.doCommit(async () => {
                return await this.bodyInternal(Promise.resolve());
            });
        }
        else {
            return Promise.resolve(false);
        }
    }

    protected doCommit(writeAction?: () => Promise<boolean>): Promise<boolean> {
        const state = this.#state;
        let allActions = Promise.resolve();
        if (state === 'new') {
            this.#state = 'committing';
            if (this.#commitActions.length > 0) {
                allActions = this.#commitActions
                    .reduce(
                        (acc, cur) => acc.then(() => cur()),
                        Promise.resolve())
                    .catch((_error) => {
                        const state = this.#state;
                        if (state === 'committing') {
                            this.#state = 'commit-action-failed';
                            // clear content headers
                        }
                    });
            }
        }
        else if (state === 'commit-action-failed') {
            this.#state = 'committing';
            // skip commit actions
        } else {
            return Promise.resolve(false);
        }

        allActions = allActions.then(() => {
            this.applyStatusCode();
            this.applyHeaders();
            this.applyCookies();
            this.#state = 'committed';
        });


        return allActions.then(async () => {
            return writeAction !== undefined ? await writeAction() : true;
        });
    }

    protected applyStatusCode(): void {}
    protected applyHeaders(): void {}
    protected applyCookies(): void {}
}
export class HttpServerRequest extends AbstractServerHttpRequest implements ServerHttpRequest {

    #url?: URL;
    #cookies?: HttpCookie[];
    readonly #req: ExtendedHttpIncomingMessage;

    constructor(req: ExtendedHttpIncomingMessage) {
        super(new IncomingMessageHeaders(req));
        this.#req = req;
    }

    getNativeRequest<T>(): T {
        return this.#req as T;
    }

    get upgrade() {
        return this.#req['upgrade'];
    }

    get http2(): boolean {
        return this.#req.httpVersionMajor >= 2;
    }

    get path() {
        return this.URL?.pathname;
    }

    get URL() {
        this.#url ??= new URL(this.#req.urlBang, `${this.protocol}://${this.host}`);
        return this.#url;
    }

    get query() {
        return this.URL?.search;
    }

    get method() {
        return this.#req.method as HttpMethod;
    }

    get host() {
        let dh: string | undefined = undefined;
        if (this.#req.httpVersionMajor >= 2) {
            dh = (this.#req.headers as http2.IncomingHttpHeaders)[':authority'];
        }
        dh ??= this.#req.socket.remoteAddress;
        return super.parseHost(dh);
    }

    get protocol() {
        let dp: string | undefined = undefined;
        if (this.#req.httpVersionMajor > 2) {
            dp = (this.#req.headers as http2.IncomingHttpHeaders)[':scheme'];
        }
        dp ??= this.#req.socketEncrypted ? 'https' : 'http';
        return super.parseProtocol(dp);
    }

    get socket() {
        return this.#req.socket;
    }

    get remoteAddress(): net.AddressInfo | undefined {
        const family = this.#req.socket.remoteFamily;
        const address = this.#req.socket.remoteAddress;
        const port = this.#req.socket.remotePort;
        const remoteAddress = (!family || !address || !port) ? undefined : { family, address, port };
        return super.parseRemoteAddress(remoteAddress) ?? remoteAddress;
    }

    get cookies(): HttpCookie[] {
        this.#cookies ??= super.cookies
        return this.#cookies;
    }

    get body(): ReadableStream<BodyChunk> {
        return http.IncomingMessage.toWeb(this.#req) as ReadableStream<BodyChunk>;
    }

    async blob() {
        const chunks: BodyChunk[] = [];
        if (this.body !== undefined) {
            for await (const chunk of this.body) {
                chunks.push(chunk);
            }
        }
        return new Blob(chunks, {type: this.headers.one('content-type') as string || 'application/octet-stream'});
    }


    async text() {
        const blob = await this.blob();
        return await blob.text();
    }

    async formData() {
        const blob = await this.blob();
        const text = await blob.text();
        return new URLSearchParams(text);
    }

    async json() {
        const blob = await this.blob();
        if (blob.size === 0) {
            return undefined;
        }
        const text = await blob.text();
        return JSON.parse(text);
    }


    protected initId(): string {
        const remoteIp = this.#req.socket.remoteAddress;
        if (!remoteIp) {
            throw new Error('Socket has no remote address');
        }
        return `${remoteIp}:${this.#req.socket.remotePort}`;
    }
}

class IncomingMessageHeaders extends AbstractHttpHeaders<ReadonlyHeaderValues> implements ReadonlyHttpHeaders {
    readonly #msg: ExtendedHttpIncomingMessage;

    constructor(msg: ExtendedHttpIncomingMessage) {
        super();
        this.#msg = msg;
    }

    has(name: string): boolean {
        return this.#msg.headers[name] !== undefined;
    }

    get(name: string): string | (readonly string[]) | undefined {
        return this.#msg.headers[name];
    }

    list(name: string): string[] {
        return super.toList(name);
    }

    one(name: string): string | undefined {
        const value = this.#msg.headers[name];
        if (Array.isArray(value)) {
            return value[0];
        }
        return value;
    }

    keys() {
        return Object.keys(this.#msg.headers).values();
    }
}

class OutgoingMessageHeaders extends AbstractHttpHeaders<HeaderValues> implements MutableHttpHeaders {
    readonly #msg: ExtendedHttpServerResponse;

    constructor(msg: ExtendedHttpServerResponse) {
        super();
        this.#msg = msg;
    }

    has(name: string): boolean {
        return this.#msg.hasHeader(name);
    }

    keys() {
        return this.#msg.getHeaderNames().values();
    }

    get(name: string): HeaderValues {
        return this.#msg.getHeader(name);
    }

    one(name: string): HeaderValue {
        const value = this.#msg.getHeader(name);
        if (Array.isArray(value)) {
            return value[0];
        }
        return value;
    }

    set(name: string, value: HeaderValues): this {
        if (!this.#msg.headersSent) {
            if (Array.isArray(value)) {
                value = value.map(v => typeof v === 'number' ? String(v) : v);
            } else if (typeof value === 'number') {
                value = String(value);
            }

            if (value) {
                this.#msg.setHeader(name, value);
            }
            else {
                this.#msg.removeHeader(name);
            }
        }
        return this;
    }

    add(name: string, value: string | (readonly string[])): this {
        if (!this.#msg.headersSent) {
            this.#msg.appendHeader(name, value);
        }
        return this;
    }

    list(name: string): string[] {
        return super.toList(name);
    }
}

export class HttpServerResponse extends AbstractServerHttpResponse implements ServerHttpResponse {
    readonly #res: ExtendedHttpServerResponse;

    constructor(res: ExtendedHttpServerResponse) {
        super(new OutgoingMessageHeaders(res));
        this.#res = res;
    }

    getNativeResponse<T>(): T {
        return this.#res as T;
    }

    get statusCode(): HttpStatusCode {
        const status = super.statusCode;
        return status ?? {value: this.#res.statusCode};
    }

    applyStatusCode() {
        const status = super.statusCode;
        if (status !== undefined) {
            this.#res.statusCode = status.value;
        }
    }

    addCookie(cookie: ResponseCookie): this {
        this.headers.add('Set-Cookie', super.setCookieValue(cookie));
        return this;
    }

    async bodyInternal(body: Promise<BodyChunk | void>): Promise<boolean> {
        if (!this.#res.headersSent) {
            if (body instanceof ReadableStream) {
                // http.IncomingMessage.fromWeb(body)
                throw new Error('ReadableStream body not supported in response');
            }
            else {
                const chunk = await body;
                return await new Promise<boolean>((resolve, reject) => {
                    try {
                        if (chunk === undefined) {
                            this.#res.end(() => {
                                resolve(true);
                            });
                        } else {
                            if (!this.headers.has('content-length')) {
                                // set content-length if not already set
                                if (typeof chunk === 'string') {
                                    this.headers.set('content-length', Buffer.byteLength(chunk));
                                }
                                else if (chunk instanceof Blob) {
                                    this.headers.set('content-length', chunk.size);
                                }
                                else {
                                    this.headers.set('content-length', chunk.byteLength);
                                }
                            }
                            this.#res.end(chunk, () => {
                                resolve(true);
                            });
                        }
                    } catch (e) {
                        reject(e instanceof Error ? e : new Error(`end failed: ${e}`));
                    }
                });
            }
        }
        else {
            return false; // already ended
        }
    }
}

export class ServerHttpRequestDecorator implements ServerHttpRequest {
    readonly #delegate: ServerHttpRequest;

    constructor(request: ServerHttpRequest) {
        this.#delegate = request;
    }

    get delegate(): ServerHttpRequest {
        return this.#delegate;
    }

    get id(): string {
        return this.#delegate.id;
    }

    get method(): HttpMethod {
        return this.#delegate.method;
    }

    get path(): string {
        return this.#delegate.path;
    }

    get protocol() {
        return this.#delegate.protocol;
    }

    get host(): string | undefined {
        return this.#delegate.host;
    }

    get URL(): URL {
        return this.#delegate.URL;
    }

    get headers(): ReadonlyHttpHeaders {
        return this.#delegate.headers;
    }

    get cookies(): HttpCookie[] {
        return this.#delegate.cookies;
    }

    get remoteAddress() {
        return this.#delegate.remoteAddress;
    }

    get upgrade() {
        return this.#delegate.upgrade;
    }

    get body(): ReadableStream<BodyChunk> | undefined {
        return this.#delegate.body;
    }
    async blob(): Promise<Blob> {
        return await this.#delegate.blob();
    }
    async text(): Promise<string> {
        return await this.#delegate.text();
    }
    async formData(): Promise<URLSearchParams> {
        return await this.#delegate.formData();
    }
    async json(): Promise<unknown> {
        return await this.#delegate.json();
    }

    toString(): string {
        return `${ServerHttpRequestDecorator.name} [delegate: ${this.delegate.toString()}]`;
    }

    public static getNativeRequest<T>(request: ServerHttpRequest): T {
        if (request instanceof AbstractServerHttpRequest) {
            return request.getNativeRequest<T>();
        }
        else if (request instanceof ServerHttpRequestDecorator) {
            return ServerHttpRequestDecorator.getNativeRequest<T>(request.delegate);
        }
        else {
            throw new Error(`Cannot get native request from ${request.constructor.name}`);
        }
    }
}
export class ServerHttpResponseDecorator implements ServerHttpResponse {
    readonly #delegate: ServerHttpResponse;

    constructor(response: ServerHttpResponse) {
        this.#delegate = response;
    }

    get delegate(): ServerHttpResponse {
        return this.#delegate;
    }

    setStatusCode(statusCode: HttpStatusCode): boolean {
        return this.delegate.setStatusCode(statusCode);
    }

    setRawStatusCode(statusCode?: number): boolean {
        return this.delegate.setRawStatusCode(statusCode);
    }

    get statusCode(): HttpStatusCode {
        return this.delegate.statusCode;
    }

    get cookies(): ResponseCookie[] {
        return this.delegate.cookies;
    }

    addCookie(cookie: ResponseCookie): this {
        this.delegate.addCookie(cookie);
        return this;
    }

    async end(): Promise<boolean> {
        return await this.delegate.end();
    }

    async body(body: ReadableStream<BodyChunk> | Promise<BodyChunk | void> | BodyChunk): Promise<boolean> {
        return await this.#delegate.body(body);
    }

    get headers(): MutableHttpHeaders {
        return this.#delegate.headers;
    }

    toString(): string {
        return `${ServerHttpResponseDecorator.name} [delegate: ${this.delegate.toString()}]`;
    }

    public static getNativeResponse<T>(response: ServerHttpResponse): T {
        if (response instanceof AbstractServerHttpResponse) {
            return response.getNativeResponse<T>();
        }
        else if (response instanceof ServerHttpResponseDecorator) {
            return ServerHttpResponseDecorator.getNativeResponse<T>(response.delegate);
        }
        else {
            throw new Error(`Cannot get native response from ${response.constructor.name}`);
        }
    }
}

export class ServerWebExchangeDecorator implements ServerWebExchange {
    readonly #delegate: ServerWebExchange;

    protected constructor(exchange: ServerWebExchange) {
        this.#delegate = exchange;
    }

    get delegate(): ServerWebExchange {
        return this.#delegate;
    }

    get request(): ServerHttpRequest {
        return this.#delegate.request;
    }

    get response(): ServerHttpResponse {
        return this.#delegate.response;
    }

    attribute<T>(name: string): T | undefined {
        return this.#delegate.attribute(name);
    }

    principal<P extends Principal>(): Promise<P | undefined> {
        return this.#delegate.principal();
    }

    get logPrefix(): string {
        return this.#delegate.logPrefix;
    }

    toString() {
        return `${ServerWebExchangeDecorator.name} [delegate: ${this.delegate}]`;
    }
}

export class DefaultWebExchange<Request extends ServerHttpRequest, Response extends ServerHttpResponse> {
    readonly request: Request;
    readonly response: Response;
    readonly #attributes: Record<string, unknown> = {};
    #logId?: unknown;
    #logPrefix: string = '';

    constructor(request: Request, response: Response) {
        this.#attributes[LOG_ID_ATTRIBUTE] = request.id;
        this.request = request;
        this.response = response;
    }

    get method(): HttpMethod {
        return this.request.method;
    }

    get path(): string | null | undefined {
        return this.request.path;
    }

    get attributes(): Record<string, unknown> {
        return this.#attributes;
    }

    attribute<T>(name: string): T | undefined {
        return this.attributes[name] as T;
    }

    principal<P extends Principal>(): Promise<P | undefined> {
        return Promise.resolve(undefined);
    }

    get logPrefix(): string {
        const value = this.attribute(LOG_ID_ATTRIBUTE);
        if (this.#logId !== value) {
            this.#logId = value;
            this.#logPrefix = value !== undefined ? `[${value}] ` : '';
        }
        return this.#logPrefix;
    }
}

export const LOG_ID_ATTRIBUTE = 'io.interop.gateway.server.log_id';

